import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import { Button } from "./ui/button";

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TYPE_COLORS = { in_person: "#007AFF", virtual: "#00CC52", evaluation: "#F97316", check_in: "#EAB308" };

function parseDate(d) {
  const parts = d.split("-");
  return new Date(+parts[0], +parts[1] - 1, +parts[2]);
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getWeekStart(d) {
  const r = new Date(d);
  r.setDate(r.getDate() - r.getDay());
  return r;
}

export default function ScheduleCalendar({ sessions = [], showClient = false }) {
  const [mode, setMode] = useState("month");
  const [current, setCurrent] = useState(new Date());
  const today = new Date();

  const sessionsByDate = useMemo(() => {
    const map = {};
    sessions.forEach(s => {
      const key = s.date;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [sessions]);

  const nav = (dir) => {
    const d = new Date(current);
    if (mode === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCurrent(d);
  };

  const title = mode === "month"
    ? `${MONTHS[current.getMonth()]} ${current.getFullYear()}`
    : (() => {
        const ws = getWeekStart(current);
        const we = new Date(ws); we.setDate(we.getDate() + 6);
        return `${MONTHS[ws.getMonth()]} ${ws.getDate()} - ${ws.getMonth() !== we.getMonth() ? MONTHS[we.getMonth()] + " " : ""}${we.getDate()}, ${we.getFullYear()}`;
      })();

  // Month grid
  const monthDays = useMemo(() => {
    if (mode !== "month") return [];
    const y = current.getFullYear(), m = current.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const days = [];
    // pad start
    for (let i = 0; i < first.getDay(); i++) {
      const d = new Date(y, m, -(first.getDay() - 1 - i));
      days.push({ date: d, outside: true });
    }
    for (let i = 1; i <= last.getDate(); i++) {
      days.push({ date: new Date(y, m, i), outside: false });
    }
    // pad end
    while (days.length % 7 !== 0) {
      const d = new Date(y, m + 1, days.length - last.getDate() - first.getDay() + 1);
      days.push({ date: d, outside: true });
    }
    return days;
  }, [current, mode]);

  // Week days
  const weekDays = useMemo(() => {
    if (mode !== "week") return [];
    const ws = getWeekStart(current);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ws); d.setDate(d.getDate() + i);
      return { date: d, outside: false };
    });
  }, [current, mode]);

  const days = mode === "month" ? monthDays : weekDays;

  const fmtKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  return (
    <div className="bg-card border border-border" data-testid="schedule-calendar">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <CalIcon className="w-4 h-4 text-[#007AFF]" />
          <h4 className="font-bold text-sm">{title}</h4>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border">
            <button onClick={() => setMode("week")}
              className={`px-3 py-1 text-xs font-medium transition-colors ${mode === "week" ? "bg-[#007AFF] text-white" : "hover:bg-secondary"}`}
              data-testid="cal-mode-week">Week</button>
            <button onClick={() => setMode("month")}
              className={`px-3 py-1 text-xs font-medium transition-colors ${mode === "month" ? "bg-[#007AFF] text-white" : "hover:bg-secondary"}`}
              data-testid="cal-mode-month">Month</button>
          </div>
          <button onClick={() => setCurrent(new Date())} className="px-2 py-1 text-xs font-medium border border-border hover:bg-secondary transition-colors" data-testid="cal-today">Today</button>
          <button onClick={() => nav(-1)} className="p-1 hover:bg-secondary transition-colors" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => nav(1)} className="p-1 hover:bg-secondary transition-colors" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-center py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div className={`grid grid-cols-7 ${mode === "week" ? "min-h-[160px]" : ""}`}>
        {days.map((day, i) => {
          const key = fmtKey(day.date);
          const daySessions = sessionsByDate[key] || [];
          const isToday = sameDay(day.date, today);
          return (
            <div key={key} className={`border-b border-r border-border p-1.5 ${mode === "month" ? "min-h-[80px]" : "min-h-[140px]"} ${day.outside ? "opacity-30" : ""}`}>
              <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center ${isToday ? "bg-[#007AFF] text-white rounded-full" : "text-muted-foreground"}`}>
                {day.date.getDate()}
              </div>
              <div className="space-y-0.5">
                {daySessions.slice(0, mode === "month" ? 2 : 5).map(s => (
                  <div key={s.id} className="flex items-center gap-1 px-1 py-0.5 text-[10px] truncate rounded-sm"
                    style={{ backgroundColor: `${TYPE_COLORS[s.type] || "#007AFF"}15`, borderLeft: `2px solid ${TYPE_COLORS[s.type] || "#007AFF"}` }}
                    title={`${s.time} - ${s.type?.replace("_", " ")}${showClient && s.client_name ? ` - ${s.client_name}` : ""}${s.notes ? ` - ${s.notes}` : ""}`}>
                    <span className="font-medium">{s.time}</span>
                    {showClient && s.client_name && <span className="truncate text-muted-foreground">{s.client_name}</span>}
                    {!showClient && <span className="truncate capitalize text-muted-foreground">{s.type?.replace("_", " ")}</span>}
                  </div>
                ))}
                {daySessions.length > (mode === "month" ? 2 : 5) && (
                  <p className="text-[9px] text-muted-foreground px-1">+{daySessions.length - (mode === "month" ? 2 : 5)} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-t border-border">
        {Object.entries(TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="capitalize">{type.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
